/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import adminApi from '../../services/adminApi';
import AdminLayout from '../../components/AdminLayout';
import {
  Settings,
  Server,
  Database,
  Cpu,
  HardDrive,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Info,
  ChevronDown,
  ChevronUp,
  Zap,
  Cloud,
  Monitor
} from 'lucide-react';
import './SystemSettings.css';

const SystemSettings = () => {
  const { user } = useAdminAuth();

  const [configs, setConfigs] = useState([]);
  const [groupedConfigs, setGroupedConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingChanges, setPendingChanges] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({
    processing: true,
    features: true,
    limits: false
  });

  // Category metadata for UI
  const categoryMeta = {
    processing: {
      label: 'Document Processing',
      icon: Cpu,
      description: 'Configure how documents are processed (Render cloud vs local worker)'
    },
    features: {
      label: 'Feature Flags',
      icon: Zap,
      description: 'Enable or disable platform features'
    },
    limits: {
      label: 'Resource Limits',
      icon: HardDrive,
      description: 'Configure resource usage limits and thresholds'
    },
    integrations: {
      label: 'Integrations',
      icon: Database,
      description: 'External service integrations'
    },
    notifications: {
      label: 'Notifications',
      icon: AlertCircle,
      description: 'Notification settings'
    },
    security: {
      label: 'Security',
      icon: Settings,
      description: 'Security-related settings'
    }
  };

  /**
   * Load all system configurations
   */
  const loadConfigs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await adminApi.getSystemConfigs();

      if (response.success) {
        setConfigs(response.configs || []);
        setGroupedConfigs(response.grouped || {});
      } else {
        setError('Failed to load configurations');
      }
    } catch (err) {
      console.error('Failed to load configs:', err);
      setError(err.response?.data?.error || 'Failed to load configurations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  /**
   * Handle config value change (locally)
   */
  const handleValueChange = (key, value) => {
    setPendingChanges(prev => ({
      ...prev,
      [key]: value
    }));
    // Clear success message when making changes
    setSuccess('');
  };

  /**
   * Get current value (pending or original)
   */
  const getCurrentValue = (config) => {
    if (pendingChanges.hasOwnProperty(config.key)) {
      return pendingChanges[config.key];
    }
    return config.value;
  };

  /**
   * Save all pending changes
   */
  const saveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      setError('No changes to save');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const updates = Object.entries(pendingChanges).map(([key, value]) => ({
        key,
        value
      }));

      const response = await adminApi.updateSystemConfigsBulk(updates, 'Updated via Admin UI');

      if (response.success) {
        setSuccess(`Successfully updated ${updates.length} configuration(s)`);
        setPendingChanges({});
        // Reload to get fresh data
        await loadConfigs();
      } else {
        setError(response.message || 'Failed to save configurations');
      }
    } catch (err) {
      console.error('Failed to save configs:', err);
      setError(err.response?.data?.error || 'Failed to save configurations');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Reset a config to default
   */
  const resetToDefault = async (key) => {
    try {
      setError('');
      const response = await adminApi.resetSystemConfig(key);

      if (response.success) {
        setSuccess(`Reset "${key}" to default value`);
        // Remove from pending changes if present
        setPendingChanges(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        await loadConfigs();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset configuration');
    }
  };

  /**
   * Discard pending changes
   */
  const discardChanges = () => {
    setPendingChanges({});
    setSuccess('Changes discarded');
  };

  /**
   * Toggle category expansion
   */
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  /**
   * Render config input based on valueType
   */
  const renderConfigInput = (config) => {
    const currentValue = getCurrentValue(config);
    const hasChange = pendingChanges.hasOwnProperty(config.key);

    switch (config.valueType) {
      case 'boolean':
        return (
          <label className={`toggle-switch ${hasChange ? 'changed' : ''}`}>
            <input
              type="checkbox"
              checked={currentValue}
              onChange={(e) => handleValueChange(config.key, e.target.checked)}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-label">{currentValue ? 'Enabled' : 'Disabled'}</span>
          </label>
        );

      case 'number':
        return (
          <div className={`number-input-wrapper ${hasChange ? 'changed' : ''}`}>
            <input
              type="number"
              value={currentValue}
              min={config.validation?.min}
              max={config.validation?.max}
              onChange={(e) => handleValueChange(config.key, parseInt(e.target.value, 10))}
            />
            {config.validation && (
              <span className="input-hint">
                Range: {config.validation.min} - {config.validation.max}
              </span>
            )}
          </div>
        );

      case 'select':
        return (
          <div className={`select-wrapper ${hasChange ? 'changed' : ''}`}>
            <select
              value={currentValue}
              onChange={(e) => handleValueChange(config.key, e.target.value)}
            >
              {config.options?.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {config.key === 'documentProcessing.mode' && (
              <div className="mode-info">
                {currentValue === 'render' && (
                  <div className="mode-badge render">
                    <Cloud size={14} /> Processing on Render (Cloud)
                  </div>
                )}
                {currentValue === 'local' && (
                  <div className="mode-badge local">
                    <Monitor size={14} /> Processing on Local Worker
                  </div>
                )}
                {currentValue === 'hybrid' && (
                  <div className="mode-badge hybrid">
                    <Server size={14} /> Hybrid (Auto-route by size)
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'string':
      default:
        return (
          <input
            type="text"
            className={hasChange ? 'changed' : ''}
            value={currentValue}
            onChange={(e) => handleValueChange(config.key, e.target.value)}
          />
        );
    }
  };

  /**
   * Render a config item
   */
  const renderConfigItem = (config) => {
    const hasChange = pendingChanges.hasOwnProperty(config.key);

    return (
      <div key={config.key} className={`config-item ${hasChange ? 'has-changes' : ''}`}>
        <div className="config-header">
          <div className="config-label">
            <span className="label-text">{config.label}</span>
            {config.requiresRestart && (
              <span className="restart-badge" title="Requires server restart">
                <RefreshCw size={12} /> Restart
              </span>
            )}
          </div>
          <button
            className="reset-btn"
            onClick={() => resetToDefault(config.key)}
            title="Reset to default"
          >
            Reset
          </button>
        </div>

        <div className="config-description">
          {config.description}
        </div>

        <div className="config-input">
          {renderConfigInput(config)}
        </div>

        {hasChange && (
          <div className="change-indicator">
            <Info size={12} /> Unsaved change
          </div>
        )}
      </div>
    );
  };

  /**
   * Render a category section
   */
  const renderCategory = (category) => {
    const categoryConfigs = groupedConfigs[category] || [];
    if (categoryConfigs.length === 0) return null;

    const meta = categoryMeta[category] || {
      label: category.charAt(0).toUpperCase() + category.slice(1),
      icon: Settings,
      description: ''
    };
    const IconComponent = meta.icon;
    const isExpanded = expandedCategories[category];

    return (
      <div key={category} className="config-category">
        <div
          className="category-header"
          onClick={() => toggleCategory(category)}
        >
          <div className="category-title">
            <IconComponent size={20} />
            <span>{meta.label}</span>
            <span className="config-count">{categoryConfigs.length}</span>
          </div>
          <div className="category-toggle">
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>

        {meta.description && (
          <div className="category-description">{meta.description}</div>
        )}

        {isExpanded && (
          <div className="category-configs">
            {categoryConfigs.map(config => renderConfigItem(config))}
          </div>
        )}
      </div>
    );
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  return (
    <AdminLayout>
      <div className="system-settings-page">
        <div className="page-header">
          <div className="header-content">
            <Settings size={28} />
            <div>
              <h1>System Settings</h1>
              <p>Configure system-wide settings and document processing options</p>
            </div>
          </div>

          <div className="header-actions">
            {hasPendingChanges && (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={discardChanges}
                  disabled={saving}
                >
                  Discard
                </button>
                <button
                  className="btn btn-primary"
                  onClick={saveChanges}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <RefreshCw size={16} className="spin" /> Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} /> Save Changes ({Object.keys(pendingChanges).length})
                    </>
                  )}
                </button>
              </>
            )}
            <button
              className="btn btn-icon"
              onClick={loadConfigs}
              disabled={loading}
              title="Refresh configurations"
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
            </button>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="alert alert-error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <CheckCircle size={16} />
            {success}
          </div>
        )}

        {/* Processing Mode Info Card */}
        <div className="info-card processing-info">
          <div className="info-icon">
            <Server size={24} />
          </div>
          <div className="info-content">
            <h3>Document Processing Mode</h3>
            <p>
              Control where document processing happens. <strong>Render</strong> processes on cloud (limited by 512MB memory),
              <strong> Local</strong> queues documents for local worker processing,
              <strong> Hybrid</strong> auto-routes large documents to local worker based on chunk threshold.
            </p>
          </div>
        </div>

        {/* Loading State */}
        {loading && configs.length === 0 && (
          <div className="loading-state">
            <RefreshCw size={32} className="spin" />
            <p>Loading configurations...</p>
          </div>
        )}

        {/* Configuration Categories */}
        {!loading && (
          <div className="config-categories">
            {['processing', 'features', 'limits', 'integrations', 'notifications', 'security'].map(category =>
              renderCategory(category)
            )}
          </div>
        )}

        {/* Pending Changes Bar */}
        {hasPendingChanges && (
          <div className="pending-changes-bar">
            <div className="pending-info">
              <AlertCircle size={16} />
              <span>You have {Object.keys(pendingChanges).length} unsaved change(s)</span>
            </div>
            <div className="pending-actions">
              <button className="btn btn-secondary" onClick={discardChanges}>
                Discard
              </button>
              <button className="btn btn-primary" onClick={saveChanges} disabled={saving}>
                <Save size={14} /> Save All
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SystemSettings;
